import { useMemo, useState } from "react";
import { X } from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Textarea } from "@/shared/components/ui/textarea";
import { normalizeTenantVariableDraft } from "@/domains/tenant";
import type {
  TenantVariableDraft,
  TenantVariableView,
  VariableType,
} from "@/domains/tenant";
import type { Json } from "@/types/supabase.types";
import { VARIABLE_TYPES } from "../variable-types";

const NO_DEFAULT_OPTION = "__none__";

const ROW_CLASS = "grid gap-2 sm:grid-cols-[160px_1fr] sm:items-center";

type TenantVariableFormValues = {
  variable_name_frontend: string;
  variable_group: string;
  variable_type: VariableType;
  variable_options: string[];
  variable_default: string;
  variable_default_boolean: boolean;
  variable_is_required: boolean;
  variable_order: string;
};

type TenantVariableFormProps = {
  initialValue?: TenantVariableView;
  isSubmitting?: boolean;
  onCancel: () => void;
  onSubmit: (draft: TenantVariableDraft) => void;
};

function parseJsonField(value: string, fallback: Json): Json {
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  try {
    return JSON.parse(trimmed) as Json;
  } catch {
    return trimmed;
  }
}

function optionsToStrings(raw: Json | null | undefined): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (typeof item === "string") return [item];
    if (item && typeof item === "object" && !Array.isArray(item)) {
      const record = item as Record<string, unknown>;
      const value = record.value ?? record.label;
      return typeof value === "string" ? [value] : [];
    }
    return [];
  });
}

function defaultToText(value: Json | null | undefined): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : JSON.stringify(item)))
      .join(", ");
  }
  return JSON.stringify(value, null, 2);
}

function createInitialValues(
  initialValue: TenantVariableView | undefined,
): TenantVariableFormValues {
  return {
    variable_name_frontend: initialValue?.variable_name_frontend ?? "",
    variable_group: initialValue?.variable_group ?? "general",
    variable_type: initialValue?.variable_type ?? "string",
    variable_options: optionsToStrings(initialValue?.variable_options),
    variable_default: defaultToText(initialValue?.variable_default),
    variable_default_boolean: initialValue?.variable_default === true,
    variable_is_required: initialValue?.variable_is_required ?? false,
    variable_order: String(initialValue?.variable_order ?? 0),
  };
}

function parseDefaultValue(values: TenantVariableFormValues): Json | null {
  if (values.variable_type === "boolean") {
    return values.variable_default_boolean;
  }

  const trimmed = values.variable_default.trim();
  if (!trimmed) return null;

  if (values.variable_type === "number" || values.variable_type === "integer") {
    const numericValue = Number(trimmed);
    if (Number.isNaN(numericValue)) throw new Error("Default must be a number");
    return numericValue;
  }

  if (values.variable_type === "json") return parseJsonField(trimmed, null);

  if (values.variable_type === "array") {
    const items = trimmed
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    if (values.variable_options.length > 0) {
      const invalid = items.filter(
        (item) => !values.variable_options.includes(item),
      );
      if (invalid.length > 0) {
        throw new Error("Default must be one of the options");
      }
    }
    return items;
  }

  return trimmed;
}

export function TenantVariableForm({
  initialValue,
  isSubmitting = false,
  onCancel,
  onSubmit,
}: TenantVariableFormProps) {
  const [values, setValues] = useState(() => createInitialValues(initialValue));
  const [optionDraft, setOptionDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const canSubmit = useMemo(
    () => values.variable_name_frontend.trim().length > 0 && !isSubmitting,
    [isSubmitting, values.variable_name_frontend],
  );

  const isArrayType = values.variable_type === "array";
  const hasOptions = isArrayType && values.variable_options.length > 0;
  const isTextareaDefault =
    values.variable_type === "text" || values.variable_type === "json";

  const updateValue = <K extends keyof TenantVariableFormValues>(
    key: K,
    value: TenantVariableFormValues[K],
  ) => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const addOption = () => {
    const option = optionDraft.trim();
    if (!option) return;
    setValues((current) =>
      current.variable_options.includes(option)
        ? current
        : {
            ...current,
            variable_options: [...current.variable_options, option],
          },
    );
    setOptionDraft("");
  };

  const removeOption = (option: string) => {
    setValues((current) => ({
      ...current,
      variable_options: current.variable_options.filter(
        (item) => item !== option,
      ),
      variable_default:
        current.variable_default.trim() === option
          ? ""
          : current.variable_default,
    }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    try {
      const order = Number(values.variable_order);
      const draft = normalizeTenantVariableDraft({
        variable_name_frontend: values.variable_name_frontend,
        variable_group: values.variable_group,
        variable_kind: initialValue?.variable_kind ?? "input",
        variable_type: values.variable_type,
        variable_options: isArrayType ? values.variable_options : [],
        variable_default: parseDefaultValue(values),
        variable_is_required: values.variable_is_required,
        variable_order: order,
      });
      onSubmit(draft);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Invalid variable");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-3">
        <div className={ROW_CLASS}>
          <Label htmlFor="variable-group">Group</Label>
          <Input
            id="variable-group"
            value={values.variable_group}
            onChange={(event) =>
              updateValue("variable_group", event.target.value)
            }
            disabled={isSubmitting}
            className="sm:max-w-sm"
          />
        </div>

        <div className={ROW_CLASS}>
          <Label htmlFor="variable-label">Label</Label>
          <Input
            id="variable-label"
            value={values.variable_name_frontend}
            onChange={(event) =>
              updateValue("variable_name_frontend", event.target.value)
            }
            disabled={isSubmitting}
            className="sm:max-w-sm"
          />
        </div>

        <div className={ROW_CLASS}>
          <Label>Type</Label>
          <Select
            value={values.variable_type}
            onValueChange={(value) =>
              updateValue("variable_type", value as VariableType)
            }
            disabled={isSubmitting}
          >
            <SelectTrigger className="w-full sm:max-w-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VARIABLE_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isArrayType && (
          <div className={`${ROW_CLASS} sm:items-start`}>
            <Label htmlFor="variable-option-draft" className="sm:pt-2.5">
              Options
            </Label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  id="variable-option-draft"
                  value={optionDraft}
                  placeholder="Add an option value"
                  onChange={(event) => setOptionDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      addOption();
                    }
                  }}
                  disabled={isSubmitting}
                  className="sm:max-w-60"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addOption}
                  disabled={isSubmitting || !optionDraft.trim()}
                >
                  Add
                </Button>
              </div>
              {values.variable_options.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {values.variable_options.map((option) => (
                    <Badge key={option} variant="secondary" className="gap-1">
                      {option}
                      <button
                        type="button"
                        onClick={() => removeOption(option)}
                        disabled={isSubmitting}
                        aria-label={`Remove ${option}`}
                        className="rounded-full hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className={ROW_CLASS}>
          <Label htmlFor="variable-order">Order</Label>
          <Input
            id="variable-order"
            type="number"
            step="1"
            value={values.variable_order}
            onChange={(event) =>
              updateValue("variable_order", event.target.value)
            }
            disabled={isSubmitting}
            className="sm:max-w-sm"
          />
        </div>

        <div className={ROW_CLASS}>
          <Label htmlFor="variable-default">Default value</Label>
          {values.variable_type === "boolean" ? (
            <label className="flex h-9 items-center gap-2 text-sm">
              <Checkbox
                checked={values.variable_default_boolean}
                onCheckedChange={(checked) =>
                  updateValue("variable_default_boolean", checked === true)
                }
                disabled={isSubmitting}
              />
              Enabled by default
            </label>
          ) : hasOptions ? (
            <Select
              value={
                values.variable_options.includes(values.variable_default.trim())
                  ? values.variable_default.trim()
                  : undefined
              }
              onValueChange={(value) =>
                updateValue(
                  "variable_default",
                  value === NO_DEFAULT_OPTION ? "" : value,
                )
              }
              disabled={isSubmitting}
            >
              <SelectTrigger
                id="variable-default"
                className="w-full sm:max-w-sm"
              >
                <SelectValue placeholder="No default" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_DEFAULT_OPTION}>No default</SelectItem>
                {values.variable_options.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : isTextareaDefault ? (
            <Textarea
              id="variable-default"
              value={values.variable_default}
              onChange={(event) =>
                updateValue("variable_default", event.target.value)
              }
              disabled={isSubmitting}
              className="font-mono text-xs"
            />
          ) : (
            <Input
              id="variable-default"
              type={
                values.variable_type === "number" ||
                values.variable_type === "integer"
                  ? "number"
                  : values.variable_type === "date"
                    ? "date"
                    : values.variable_type === "datetime"
                      ? "datetime-local"
                      : "text"
              }
              value={values.variable_default}
              onChange={(event) =>
                updateValue("variable_default", event.target.value)
              }
              placeholder={
                isArrayType ? "Comma-separated values" : "No default"
              }
              disabled={isSubmitting}
              className="sm:max-w-sm"
            />
          )}
        </div>

        <div className={ROW_CLASS}>
          <Label htmlFor="variable-required">Required</Label>
          <div className="flex h-9 items-center">
            <Checkbox
              id="variable-required"
              checked={values.variable_is_required}
              onCheckedChange={(checked) =>
                updateValue("variable_is_required", checked === true)
              }
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          Save variable
        </Button>
      </div>
    </form>
  );
}
